/**
 * Pure resolution of the effective marketing-consent decision (extracted from `AnalyticsConsent`
 * so it can be unit-tested without rendering — see `react` standards).
 *
 * Policy: tracking (Google Analytics + the Facebook Pixel + the Reddit Pixel) is ON BY DEFAULT.
 * The marketing site MAY hand off an explicit consent choice via `startProperties.consent`, and the
 * owner MAY override it in Settings › Privacy; either explicit choice is respected. Only when NO
 * choice exists anywhere do we fall back to the default (on).
 *
 * The precedence, highest first:
 *   1. the saved user-preferences `privacy.marketing` (cross-device, from Settings or the one-time
 *      seed of the hand-off);
 *   2. the browser-scoped hand-off choice (`getTrackingConsent()` → localStorage / startProperties);
 *   3. the default — ON.
 *
 * `??` (not `||`) is load-bearing at each step: a saved / handed-off `false` is an explicit opt-out
 * and must win over the lower-precedence sources AND over the default.
 */
export interface MarketingConsentInputs {
  /** A resolved owner identity is present (a real `user_id`). */
  authenticated: boolean;
  /** The cross-device user-preferences have finished loading. */
  prefsLoaded: boolean;
  /** Saved `privacy.marketing`, or `null` / `undefined` when the namespace is unset. */
  savedMarketing: boolean | null | undefined;
  /** Hand-off `marketing` (localStorage / startProperties), or `null` / `undefined` when none. */
  handoffMarketing: boolean | null | undefined;
}

export function resolveMarketingConsent({
  authenticated,
  prefsLoaded,
  savedMarketing,
  handoffMarketing,
}: MarketingConsentInputs): boolean {
  // Authenticated but the authoritative saved choice hasn't loaded yet: deny transiently so we
  // never fire on a possibly-stale value and then have to revoke once the saved `false` lands.
  // (Unauthenticated visitors have no saved choice to wait for, so they get the default immediately.)
  if (authenticated && !prefsLoaded) return false;
  return savedMarketing ?? handoffMarketing ?? true;
}
